<template>
  <div class="support-conversation">
    <div class="conversation-header">
      <h3 class="conversation-title">
        <v-icon left color="#EA5545">mdi-chat</v-icon>
        {{ t('supportConversation.title') }}
      </h3>
      <div class="conversation-tools">
        <span v-if="messages.length > 0" class="message-count">
          {{ messages.length }} {{ t('supportConversation.messageLabel') }}
        </span>
        <div class="sort-buttons" v-if="messages.length > 1">
          <v-btn
            size="small"
            variant="text"
            :color="sortOrder === 'asc' ? '#EA5545' : '#94a3b8'"
            @click="setSortOrder('asc')"
          >
            <v-icon left size="16">mdi-arrow-up</v-icon>
            {{ t('supportConversation.sort.oldest') }}
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            :color="sortOrder === 'desc' ? '#EA5545' : '#94a3b8'"
            @click="setSortOrder('desc')"
          >
            <v-icon left size="16">mdi-arrow-down</v-icon>
            {{ t('supportConversation.sort.newest') }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Unread Messages Alert -->
    <v-alert
      v-if="unreadCount > 0"
      type="info"
      color="#EA5545"
      variant="tonal"
      closable
      class="unread-alert mb-4"
    >
      <div class="unread-alert-content">
        <v-icon size="20" class="mr-2">mdi-message-badge</v-icon>
        <span>
          <strong>{{ t('supportConversation.newAlert', { count: unreadCount }) }}</strong>
        </span>
      </div>
    </v-alert>

    <!-- Messages List -->
    <div v-if="isLoading" class="loading-state">
      <v-progress-circular indeterminate color="#EA5545" size="40"></v-progress-circular>
      <p class="state-text">{{ t('supportConversation.loading') }}</p>
    </div>

    <div v-else-if="messages.length > 0" class="messages-container">
      <template v-for="(message, index) in messagesWithUnreadFlag" :key="message.talk_id">
        
        <!-- Unread Messages Divider -->
        <div 
          v-if="isFirstUnreadMessage(message, index)"
          class="unread-divider"
        >
          <div class="unread-divider-line"></div>
          <span class="unread-divider-text">
            <v-icon size="16" color="#EA5545">mdi-message-badge</v-icon>
            {{ t('supportConversation.dividerNew') }}
          </span>
          <div class="unread-divider-line"></div>
        </div>
        
        <div 
          :class="[
            'message-card', 
            { 
              'admin-message': message.user_admin === 1,
              'unread-message': message.is_unread === true || message.is_unread === 1
            }
          ]"
        >
        <div class="message-header">
          <div class="user-info">
            <div :class="['user-avatar', { 'admin-avatar': message.user_admin === 1 }]">
              <img
                v-if="message.user_thumb"
                :src="getUserThumb(message.user_thumb)"
                alt="Avatar"
              />
              <v-icon v-else-if="message.user_admin === 1" color="white" size="18">mdi-shield-account</v-icon>
              <v-icon v-else color="white" size="18">mdi-account</v-icon>
            </div>
            <div class="user-details">
              <span class="user-name">
                {{ message.user_admin_name || message.user_name || 'Anonymous' }}
                <v-chip 
                  v-if="message.user_admin === 1" 
                  size="x-small" 
                  color="#EA5545"
                  class="admin-badge ml-2"
                >
                  Admin
                </v-chip>
                <v-chip 
                  v-if="message.is_unread === true || message.is_unread === 1" 
                  size="x-small" 
                  color="#EA5545"
                  class="new-badge ml-2"
                >
                  NEW
                </v-chip>
              </span>
              <span class="message-time">{{ formatDate(message.talk_created_date) }}</span>
            </div>
          </div>
        </div>
        
        <div class="message-content">
          <p class="message-text">{{ message.talk_text }}</p>
          
          <!-- Message Files -->
          <div v-if="message.files && message.files.length > 0" class="message-files">
            <div 
              v-for="file in message.files" 
              :key="file.file_id"
              class="message-file-item"
            >
              <div class="file-item-content" @click="openFile(file)">
                <v-icon :color="getFileIconColor(file.file_ext)" size="20">{{ getFileIcon(file.file_ext) }}</v-icon>
                <span class="file-item-name">
                  {{ file.file_name }}
                  <v-chip
                    v-if="file.file_is_admin === 1 || file.file_is_admin === '1'"
                    size="x-small"
                    color="#EA5545"
                    class="ml-2"
                  >
                    Admin
                  </v-chip>
                </span>
                <v-icon size="16" color="#999">mdi-download</v-icon>
              </div>
              <v-btn 
                icon 
                size="x-small" 
                variant="text"
                color="error"
                @click.stop="confirmDeleteFile(file)"
                class="file-delete-btn"
              >
                <v-icon size="16">mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
        </div>
      </template>
    </div>

    <div v-else class="empty-conversation">
      <v-icon size="48" color="#ccc">mdi-chat-outline</v-icon>
      <p class="state-text">{{ t('supportConversation.emptyDescription') }}</p>
    </div>

    <v-divider class="my-4"></v-divider>

    <!-- New Message Form -->
    <div class="new-message-form">
      <h4 class="form-title">{{ t('supportConversation.formTitle') }}</h4>
      <v-textarea
        v-model="newMessage"
        :placeholder="t('supportConversation.placeholder')"
        rows="4"
        variant="outlined"
        hide-details
        :disabled="isSending"
      ></v-textarea>
      
      <!-- File Upload Section -->
      <div class="upload-section mt-4">
        <input 
          ref="messageFileInput"
          type="file" 
          multiple 
          @change="handleMessageFileSelect"
          style="display: none"
        />
        
        <v-btn 
          variant="outlined"
          color="#EA5545" 
          @click="$refs.messageFileInput.click()"
          :disabled="isSending"
          size="small"
        >
          <v-icon left size="18">mdi-paperclip</v-icon>
          {{ t('supportConversation.attach') }}
        </v-btn>
        
        <span v-if="messageFiles.length > 0" class="file-count ml-3">
          <template v-if="messageFiles.length === 1">
            {{ messageFiles[0].name }}
          </template>
          <template v-else>
            {{ messageFiles.length }} files selected
          </template>
        </span>
      </div>

      <!-- Selected Files Preview -->
      <div v-if="messageFiles.length > 0" class="selected-files-preview mt-3">
        <div v-for="(file, index) in messageFiles" :key="index" class="file-preview-item">
          <v-icon color="#666" size="18">mdi-file</v-icon>
          <span class="file-preview-name">{{ file.name }}</span>
          <span class="file-preview-size">{{ formatFileSize(file.size) }}</span>
          <v-btn 
            icon 
            size="x-small" 
            variant="text"
            @click="removeMessageFile(index)"
            :disabled="isSending"
          >
            <v-icon size="16">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      
      <div v-if="messageError" class="error-message mt-2">
        <v-icon left color="error" size="18">mdi-alert-circle</v-icon>
        <span>{{ messageError }}</span>
      </div>

      <div class="form-actions">
        <v-btn 
          color="#EA5545" 
          @click="sendMessage"
          :loading="isSending"
          :disabled="!newMessage.trim() || isSending"
        >
          <v-icon left>mdi-send</v-icon>
          {{ t('supportConversation.send') }}
        </v-btn>
      </div>
    </div>

    <!-- File Preview Dialog -->
    <v-dialog v-model="showFileDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="file-dialog-title">
          <span>{{ selectedFile?.file_name }}</span>
          <v-btn icon @click="showFileDialog = false" variant="text">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="file-dialog-content">
          <div v-if="selectedFile" class="file-preview-container">
            <!-- Image Preview -->
            <div v-if="isImage(selectedFile.file_ext)" class="image-preview">
              <img :src="getFileUrl(selectedFile)" :alt="selectedFile.file_name" />
            </div>
            
            <!-- PDF Preview -->
            <div v-else-if="selectedFile.file_ext === 'pdf'" class="pdf-preview">
              <iframe :src="getFileUrl(selectedFile)" width="100%" height="600px"></iframe>
            </div>
            
            <!-- Other Files -->
            <div v-else class="file-download-info">
              <v-icon size="80" :color="getFileIconColor(selectedFile.file_ext)">
                {{ getFileIcon(selectedFile.file_ext) }}
              </v-icon>
              <h3>{{ selectedFile.file_name }}</h3>
              <p class="file-type-text">{{ selectedFile.file_ext.toUpperCase() }} File</p>
              <v-btn 
                color="#EA5545" 
                :href="getFileUrl(selectedFile)" 
                target="_blank"
                download
                class="mt-4"
              >
                <v-icon left>mdi-download</v-icon>
                Download File
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, inject, defineProps } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

const props = defineProps({
  supportId: {
    type: [Number, String],
    required: true
  },
  last_read_date: {
    type: String
  }
})

const baseCalls = inject('baseCalls')
const successStore = inject('successStore')
const { t } = useLanguage()

// State
const messages = ref([])
const newMessage = ref('')
const isLoading = ref(false)
const isSending = ref(false)
const messageError = ref('')
const messageFiles = ref([])
const messageFileInput = ref(null)
const showFileDialog = ref(false)
const selectedFile = ref(null)
const hasScrolledToUnread = ref(false)
const lastReadDate = ref('1973-01-01 00:00:00')
const currentUserId = ref(0)
const sortOrder = ref('asc')

// Computed
const sortedMessages = computed(() => {
  const sorted = [...messages.value].sort((a, b) => {
    return new Date(a.talk_created_date) - new Date(b.talk_created_date)
  })
  return sortOrder.value === 'asc' ? sorted : sorted.reverse()
})

const messagesWithUnreadFlag = computed(() => {
  const now = new Date()
  
  return sortedMessages.value.map(message => {
    const messageDate = new Date(message.talk_created_date)
    const readDate = new Date(lastReadDate.value)
    
    // Markiere als ungelesen wenn:
    // 1. Von anderem User (talk_user_id != current_user_id)
    // 2. Nach letztem Lesedatum erstellt (talk_created_date > last_read_date)
    // 3. Nachricht ist nicht in der Zukunft (talk_created_date <= now)
    const isUnread = (
      message.talk_user_id != currentUserId.value && 
      messageDate > readDate &&
      messageDate <= now
    )
    
    return {
      ...message,
      is_unread: isUnread
    }
  })
})

const unreadCount = computed(() => {
  return messagesWithUnreadFlag.value.filter(m => m.is_unread).length
})

// Functions
function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return 'Just now'
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now - date
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInHours < 24) return `${diffInHours}h ago`
  if (diffInDays < 7) return `${diffInDays}d ago`
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

async function loadMessages() {
  isLoading.value = true
  messageError.value = ''
  hasScrolledToUnread.value = false
  
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getsupporttalks',
      sup_id: props.supportId
    })
    
    if (response.data.success && response.data.talks) {
      messages.value = response.data.talks
      
      // Use prop if provided, otherwise use response
      if (!props.last_read_date && response.data.last_read_date) {
        lastReadDate.value = response.data.last_read_date
      }
      
      currentUserId.value = response.data.current_user_id || 0
      
      // Scroll to first unread message after DOM update
      setTimeout(() => {
        scrollToFirstUnread()
      }, 100)
    } else {
      messages.value = []
    }
  } catch (err) {
    console.error('Error loading messages:', err)
    messageError.value = t('supportConversation.errors.loadFailed')
  } finally {
    isLoading.value = false
  }
}

async function markAsRead() {
  try {
    await baseCalls.clearmentPost({
      action: 'marksupportasread',
      sup_id: props.supportId
    })
  } catch (err) {
    console.error('Error marking as read:', err)
  }
}

function scrollToFirstUnread() {
  if (hasScrolledToUnread.value) return
  
  // Try to scroll to divider first, otherwise to first unread message
  const divider = document.querySelector('.unread-divider')
  const firstUnread = document.querySelector('.unread-message')
  
  if (divider) {
    divider.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    })
    hasScrolledToUnread.value = true
  } else if (firstUnread) {
    firstUnread.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    })
    hasScrolledToUnread.value = true
  }
}

function isFirstUnreadMessage(message, index) {
  // Check if this is the first unread message
  if (!message.is_unread) {
    return false
  }
  
  // Check if previous message was read (or is the first message)
  if (index === 0) {
    return true
  }
  
  const previousMessage = messagesWithUnreadFlag.value[index - 1]
  return !previousMessage.is_unread
}

async function sendMessage() {
  if (!newMessage.value.trim()) {
    messageError.value = t('supportConversation.errorRequired')
    return
  }

  isSending.value = true
  messageError.value = ''

  try {
    // First, send the message
    const response = await baseCalls.clearmentPost({
      action: 'insertsupporttalk',
      sup_id: props.supportId,
      supt_text: newMessage.value.trim()
    })

    if (response.data.success) {
      const talkId = response.data.talk.talk_id
      
      // If there are files, upload them with the talk_id
      if (messageFiles.value.length > 0) {
        await uploadMessageFiles(talkId)
      }
      
      successStore.setSuccess(true, 'Message Sent', 'Your message has been added to the conversation')
      newMessage.value = ''
      messageFiles.value = []
      if (messageFileInput.value) {
        messageFileInput.value.value = ''
      }
      
      // Reload messages
      await loadMessages()
    } else {
      messageError.value = response.data.message || t('supportConversation.errors.sendFailed')
    }
  } catch (err) {
    console.error('Error sending message:', err)
    messageError.value = t('supportConversation.errors.sendException')
  } finally {
    isSending.value = false
  }
}

async function uploadMessageFiles(talkId) {
  if (messageFiles.value.length === 0 || !talkId) return

  try {
    const formData = new FormData()
    
    // Add all files to FormData
    messageFiles.value.forEach(file => {
      formData.append('files[]', file)
    })
    
    // Add support ticket ID and talk ID
    formData.append('file_support_id', props.supportId)
    formData.append('file_talk_id', talkId)

    const response = await baseCalls.clearmentUpload(formData)

    if (!response.data.success) {
      console.error('Some files failed to upload:', response.data)
    }
  } catch (err) {
    console.error('Error uploading files:', err)
  }
}

function handleMessageFileSelect(event) {
  const files = Array.from(event.target.files)
  messageFiles.value = files
}

function removeMessageFile(index) {
  messageFiles.value.splice(index, 1)
  if (messageFiles.value.length === 0 && messageFileInput.value) {
    messageFileInput.value.value = ''
  }
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

function openFile(file) {
  selectedFile.value = file
  showFileDialog.value = true
}

function confirmDeleteFile(file) {
  if (confirm(`Are you sure you want to delete "${file.file_name}"? This action cannot be undone.`)) {
    deleteFileFromMessage(file.file_id)
  }
}

async function deleteFileFromMessage(fileId) {
  try {
    const response = await baseCalls.clearmentPost({
      action: 'deletefile',
      file_id: fileId
    })
    
    if (response.data.success) {
      successStore.setSuccess(true, 'File Deleted', 'The file has been deleted successfully')
      
      // Remove file from messages array
      messages.value.forEach(message => {
        if (message.files) {
          message.files = message.files.filter(f => f.file_id != fileId)
        }
      })
    } else {
      console.error('Failed to delete file:', response.data.message)
    }
  } catch (err) {
    console.error('Error deleting file:', err)
  }
}

function getFileUrl(file) {
  // Use file hash for secure file access
  return `/getfile/${file.file_hash}`
}

function isImage(ext) {
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']
  return imageExts.includes(ext.toLowerCase())
}

function getFileIcon(ext) {
  const icons = {
    pdf: 'mdi-file-pdf-box',
    doc: 'mdi-file-word',
    docx: 'mdi-file-word',
    xls: 'mdi-file-excel',
    xlsx: 'mdi-file-excel',
    ppt: 'mdi-file-powerpoint',
    pptx: 'mdi-file-powerpoint',
    txt: 'mdi-file-document',
    zip: 'mdi-zip-box',
    rar: 'mdi-zip-box',
    jpg: 'mdi-file-image',
    jpeg: 'mdi-file-image',
    png: 'mdi-file-image',
    gif: 'mdi-file-image',
    mp4: 'mdi-file-video',
    mp3: 'mdi-file-music',
  }
  return icons[ext.toLowerCase()] || 'mdi-file'
}

function getFileIconColor(ext) {
  const colors = {
    pdf: '#d32f2f',
    doc: '#1976d2',
    docx: '#1976d2',
    xls: '#388e3c',
    xlsx: '#388e3c',
    ppt: '#f57c00',
    pptx: '#f57c00',
    jpg: '#7b1fa2',
    jpeg: '#7b1fa2',
    png: '#7b1fa2',
    gif: '#7b1fa2',
    zip: '#616161',
    rar: '#616161',
  }
  return colors[ext.toLowerCase()] || '#666'
}

function getUserThumb(hash) {
  if (!hash) return null
  return `/getfile/${hash}`
}

function setSortOrder(order) {
  sortOrder.value = order
}

// Watch for last_read_date prop changes
watch(() => props.last_read_date, (newDate) => {
  if (newDate) {
    lastReadDate.value = newDate
  }
}, { immediate: true })

// Watch for support ID changes
watch(() => props.supportId, (newId) => {
  if (newId) {
    loadMessages()
  }
}, { immediate: true })

// Mark as read after user has viewed messages
let markAsReadTimeout = null

watch(unreadCount, (newCount) => {
  // If there are unread messages and user has scrolled to them
  if (newCount > 0 && hasScrolledToUnread.value) {
    // Mark as read after 3 seconds of viewing
    if (markAsReadTimeout) {
      clearTimeout(markAsReadTimeout)
    }
    
    markAsReadTimeout = setTimeout(() => {
      markAsRead()
    }, 3000)
  }
})

// Initial load
onMounted(() => {
  loadMessages()
})

// Cleanup and mark as read when leaving
onUnmounted(() => {
  if (markAsReadTimeout) {
    clearTimeout(markAsReadTimeout)
  }
  // Mark as read when leaving the conversation
  if (unreadCount.value > 0) {
    markAsRead()
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/objects/SupportConversation.css"></style>
